import { BudgetStatus, Project } from '../../../shared/types'
import { getDateKeyInTimezone } from '../../../shared/utils/dateUtils'
import { getEffectiveTimezone } from './timezoneOptions'
import { getCurrencyByCode } from './currency'

/** Returns the JS Date.getDay() value (0=Sun…6=Sat) from a YYYY-MM-DD dateKey. */
export function getDayOfWeekFromDateKey(dateKey: string): number {
  const [y, m, d] = dateKey.split('-').map(Number)
  return new Date(y!, m! - 1, d!).getDay()
}

/** True when the date falls on a day the user has excluded from budget checks. */
export function isExcludedBudgetDay(dateKey: string, excludedDays: number[]): boolean {
  if (!excludedDays.length) return false
  return excludedDays.includes(getDayOfWeekFromDateKey(dateKey))
}

/**
 * Calculate hourly budget status for a given number of hours worked.
 * granularity scales the daily budget for weekly (×5) or monthly (×22) views.
 * Empty days (0 hours) are always considered 'in' budget.
 */
export function calculateHourlyBudgetStatus(
  project: Pick<Project, 'budget' | 'budgetType' | 'budgetHourLimit'>,
  hoursWorked: number,
  granularity: 'daily' | 'weekly' | 'monthly' = 'daily'
): BudgetStatus {
  if (!project.budget || project.budgetType !== 'hours') return 'in'
  if (hoursWorked === 0) return 'in' // empty day → never flag as under

  const dailyBudget = project.budget
  const toleranceHours = (project.budgetHourLimit ?? 60) / 60

  const scaleFactor = granularity === 'weekly' ? 5 : granularity === 'monthly' ? 22 : 1
  const scaledBudget = dailyBudget * scaleFactor
  const scaledTolerance = toleranceHours * scaleFactor

  if (hoursWorked > scaledBudget + scaledTolerance) return 'over'
  if (hoursWorked < scaledBudget - scaledTolerance) return 'under'
  return 'in'
}

/**
 * Returns 'over' once cumulativeSpent exceeds the project's monetary budget.
 * Returns 'in' otherwise (including when no monetary budget is configured).
 */
export function calculateMonetaryBudgetStatus(
  project: Pick<Project, 'budget' | 'budgetType'>,
  cumulativeSpent: number
): 'over' | 'in' {
  if (!project.budget || project.budgetType !== 'monetary') return 'in'
  return cumulativeSpent > project.budget ? 'over' : 'in'
}

/**
 * Returns a human-readable tooltip string describing a budget violation.
 * Returns empty string when status is 'in'.
 */
export function getBudgetTooltipText(
  project: Pick<Project, 'budget' | 'budgetType' | 'budgetHourLimit'>,
  status: BudgetStatus,
  hoursWorked?: number,
  cumulativeSpent?: number
): string {
  if (status === 'in') return ''

  if (project.budgetType === 'hours') {
    const budget = project.budget ?? 0
    const tol = (project.budgetHourLimit ?? 60) / 60
    const worked = hoursWorked !== undefined ? hoursWorked.toFixed(1) : '?'
    return status === 'over'
      ? `Over budget: worked ${worked}h, budget ${budget}h ± ${tol.toFixed(1)}h`
      : `Under budget: worked ${worked}h, budget ${budget}h ± ${tol.toFixed(1)}h`
  }

  if (project.budgetType === 'monetary') {
    const spent = cumulativeSpent !== undefined ? cumulativeSpent.toFixed(2) : '?'
    return `Over budget: €${spent} spent, budget €${project.budget}`
  }

  return ''
}

/**
 * Resolves the effective hourly rate for an entry, preferring
 * location-specific rates over the project default.
 */
export function getEffectiveHourlyRate(
  project: Pick<Project, 'hourlyRate' | 'locations'>,
  location?: string
): number {
  if (location && project.locations) {
    const locationConfig = project.locations.find(loc => loc.name === location)
    if (locationConfig?.hourlyRate !== undefined) {
      return locationConfig.hourlyRate
    }
  }
  return project.hourlyRate ?? 0
}

/**
 * Formats a monetary amount with currency symbol using the locale appropriate
 * for the given currency (e.g. de-DE for EUR, en-US for USD).
 */
export function formatCurrency(amount: number, currency: string): string {
  const locale = getCurrencyByCode(currency).locale
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

/**
 * Formats a rate (price per hour) with currency symbol using the locale
 * appropriate for the given currency.
 * Whole numbers show no decimals (€100), fractional values show exactly 2 (€100,50).
 * The "/h" suffix is NOT included — callers append it as needed.
 */
export function formatRate(amount: number, currency: string): string {
  const locale = getCurrencyByCode(currency).locale
  const isWhole = amount % 1 === 0;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

/**
 * Aggregates revenue per currency from an array of entries.
 * Entries with null revenue or currency are ignored.
 * Returns a Map<currencyCode, totalRevenue>.
 */
export function aggregateRevenueByCurrency(
  entries: Array<{ revenue: number | null; currency: string | null }>
): Map<string, number> {
  const totals = new Map<string, number>()
  for (const entry of entries) {
    if (entry.revenue !== null && entry.currency) {
      totals.set(entry.currency, (totals.get(entry.currency) ?? 0) + entry.revenue)
    }
  }
  return totals
}

/**
 * Formats a revenue totals map as a display string using smart decimal formatting
 * (whole numbers show no decimals, fractional values show 2 decimal places).
 * Single currency: "€600" or "€150.50"
 * Multiple currencies: "€600 / $75.50"
 * Empty map: ""
 */
export function formatRevenueSummary(totals: Map<string, number>): string {
  if (totals.size === 0) return ''
  return Array.from(totals.entries())
    .map(([currency, amount]) => formatRate(amount, currency))
    .join(' / ')
}

/**
 * Computes the Set of entry IDs whose day/project combination violates budget
 * (over or under for hourly budgets, over for monetary budgets).
 *
 * Uses the same logic as budgetRowStatusTask in Reporting.tsx so that all
 * views show consistent indicators.
 */
export function computeBudgetViolatingEntryIds<
  TEntry extends {
    id: string
    projectId?: string
    startTime: string
    endTime?: string
    location?: string
    timezone?: string
  }
>(
  entries: TEntry[],
  projects: Project[],
  getCurrentDuration: (entry: TEntry) => number
): Set<string> {
  const violatingIds = new Set<string>()
  if (!entries.length || !projects.length) return violatingIds

  const projectMap = new Map(projects.map(p => [p.id, p]))

  // --- Hourly budget: group entries by project+date, accumulate totals ---
  const hourlyGroups = new Map<string, { project: Project; entries: TEntry[]; totalHours: number }>()
  for (const entry of entries) {
    const project = entry.projectId ? projectMap.get(entry.projectId) : undefined
    if (!project || project.budgetType !== 'hours') continue
    const tz = getEffectiveTimezone(entry.timezone)
    const dateKey = getDateKeyInTimezone(new Date(entry.startTime), tz)
    if (isExcludedBudgetDay(dateKey, project.budgetExcludedDays ?? [0, 6])) continue
    const key = `${entry.projectId}|${dateKey}`
    const hours = getCurrentDuration(entry) / 3600
    const existing = hourlyGroups.get(key)
    if (existing) {
      existing.entries.push(entry)
      existing.totalHours += hours
    } else {
      hourlyGroups.set(key, { project, entries: [entry], totalHours: hours })
    }
  }

  for (const { project, entries: group, totalHours } of hourlyGroups.values()) {
    const status = calculateHourlyBudgetStatus(project, totalHours)
    if (status === 'in') continue

    if (status === 'under') {
      // Under budget: flag only the last entry of the day (the one that finalises
      // the day as under-budget — symmetric to the crossover logic for over-budget)
      const sorted = [...group].sort(
        (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      )
      const last = sorted[sorted.length - 1]
      if (last) violatingIds.add(last.id)
    } else {
      // Over budget: flag only the entry that first pushes the running total
      // past budget + tolerance (sorted ascending by startTime)
      const sorted = [...group].sort(
        (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      )
      const threshold = (project.budget ?? 0) + (project.budgetHourLimit ?? 60) / 60
      let running = 0
      let found = false
      for (const entry of sorted) {
        running += getCurrentDuration(entry) / 3600
        if (running > threshold) {
          violatingIds.add(entry.id)
          found = true
          break
        }
      }
      // Fallback: guard against floating-point edge cases
      if (!found && sorted.length > 0) violatingIds.add(sorted[sorted.length - 1]!.id)
    }
  }

  // --- Monetary budget: accumulate cost chronologically per project ---
  const projectsWithMonetary = [...projectMap.values()].filter(
    p => p.budgetType === 'monetary' && p.budget
  )
  for (const project of projectsWithMonetary) {
    const projectEntries = entries
      .filter(e => e.projectId === project.id)
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
    let cumulative = 0
    for (const entry of projectEntries) {
      const hours = getCurrentDuration(entry) / 3600
      const rate = getEffectiveHourlyRate(project, entry.location)
      cumulative += hours * rate
      if (calculateMonetaryBudgetStatus(project, cumulative) === 'over') {
        violatingIds.add(entry.id)
      }
    }
  }

  return violatingIds
}
